/**
 * Multi-Factor Authentication (MFA) type contracts.
 *
 * These types define the shape of MFA data structures so that future MFA
 * implementation work has a stable contract to build against, and current
 * auth flows can be designed with MFA extension points in mind.
 *
 * NOTE: This file intentionally contains **types only** — no runtime code,
 * no database migrations, and no enforcement logic. Implementation is out
 * of scope for the current epic and will be introduced in a follow-up.
 *
 * ## Integration points
 *
 * 1. **Login flow** — After a successful credential check but **before**
 *    session creation, the auth service should check whether the user has
 *    any enrolled {@link UserMfa} records. If so, issue an
 *    {@link MfaChallenge}, return it to the client, and defer session
 *    creation until the challenge is verified.
 *
 * 2. **Sensitive actions (step-up authentication)** — For high-risk
 *    operations (e.g. changing email, disabling MFA, admin actions),
 *    the handler should require a fresh {@link MfaChallenge} verification
 *    even if the user already has an active session.
 */

/**
 * Supported MFA methods.
 *
 * - `TOTP` — Time-based One-Time Password (e.g. Google Authenticator, Authy).
 * - `SMS`  — One-time code delivered via SMS.
 * - `EMAIL` — One-time code delivered via email.
 */
export type MfaMethod = 'TOTP' | 'SMS' | 'EMAIL';
export const MfaMethod = {
  TOTP: 'TOTP',
  SMS: 'SMS',
  EMAIL: 'EMAIL',
} as const satisfies Record<MfaMethod, MfaMethod>;

/**
 * Per-user MFA enrollment record.
 *
 * Represents a single enrolled MFA method for a user. A user may have
 * multiple `UserMfa` records (one per method) once multi-method support
 * is implemented.
 */
export interface UserMfa {
  /** Owning user id (FK to `User.id`). */
  userId: string;

  /** Which MFA method this record represents. */
  method: MfaMethod;

  /**
   * Method-specific secret material.
   *
   * - For {@link MfaMethod.TOTP}, this is the shared TOTP secret.
   * - For {@link MfaMethod.SMS} / {@link MfaMethod.EMAIL}, this is the
   *   destination (phone number / email) used for delivery.
   *
   * **MUST** be encrypted at rest using the application KMS key before
   * being persisted. Never log or return this field over the wire.
   */
  secret: string;

  /** Timestamp the user completed enrollment for this method. */
  enrolledAt: Date;

  /**
   * Timestamp the method was last used to satisfy a challenge.
   * `null` if the method has never been used since enrollment.
   */
  lastUsedAt: Date | null;

  /**
   * One-time backup / recovery codes the user can use if the primary
   * method is unavailable. **MUST** be stored hashed (e.g. bcrypt/argon2)
   * — never in plaintext. Each code is single-use.
   */
  backupCodes: string[];
}

/**
 * An in-flight MFA challenge issued during login or step-up authentication.
 *
 * A challenge is created when a protected flow needs to verify possession
 * of a second factor. The client receives the `challengeId` and submits it
 * alongside the user-provided code to complete verification.
 */
export interface MfaChallenge {
  /** Opaque, server-generated challenge identifier. */
  challengeId: string;

  /** Which method the user is being challenged with. */
  method: MfaMethod;

  /**
   * Absolute expiry timestamp. Challenges that are not verified before
   * this time MUST be rejected by the verification endpoint.
   */
  expiresAt: Date;

  /**
   * Whether the challenge has been successfully verified.
   * Transitions from `false` → `true` exactly once; a verified challenge
   * MUST NOT be reusable.
   */
  verified: boolean;
}
